import React, { useRef, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Play, Pause, Volume2, VolumeX, Music2 } from "lucide-react";

const TRACK = {
  title: "Everythang Redneck",
  subtitle: "The Anthem · Theme Song",
  url: "/api/audio/everything-redneck-theme.mp3",
};

// Routes where the floating theme player should NOT appear.
// /music has its own streaming buttons — theme song would compete with them
// and give away free audio the customer wants funnelled to Spotify/Apple.
const HIDDEN_ROUTES = ["/music"];

export default function ThemePlayer() {
  const location = useLocation();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [progress, setProgress] = useState(0);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const hidden = HIDDEN_ROUTES.includes(location.pathname);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const tick = () => {
      if (a.duration) setProgress((a.currentTime / a.duration) * 100);
    };
    a.addEventListener("timeupdate", tick);
    a.addEventListener("ended", () => setPlaying(false));
    return () => {
      a.removeEventListener("timeupdate", tick);
    };
  }, []);

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      try { await a.play(); setPlaying(true); } catch (err) {
        // Browsers block audio autoplay without user gesture — this is expected.
        console.debug("Theme song autoplay blocked (normal):", err?.message);
      }
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !audioRef.current.muted;
      setMuted(audioRef.current.muted);
    }
  };

  // Return null on hidden routes so the <audio> element is fully unmounted —
  // this simultaneously guarantees zero background playback AND removes the
  // audio element from the DOM (so /music can't be reverse-engineered).
  if (hidden) return null;

  return (
    <div className="fixed bottom-4 left-4 z-40" data-testid="theme-player">
      <audio ref={audioRef} src={`${BACKEND}${TRACK.url}`} preload="metadata" loop />
      {expanded ? (
        <div className="bg-[#151A14] border-2 border-[#DD5422] p-3 w-72 shadow-2xl">
          <div className="flex items-center gap-3">
            <button
              data-testid="theme-play-toggle"
              onClick={toggle}
              className="w-12 h-12 bg-[#DD5422] flex items-center justify-center hover:bg-[#FF6B35] transition-colors flex-shrink-0"
              aria-label={playing ? "Pause" : "Play"}
            >
              {playing ? (
                <Pause className="w-5 h-5 text-[#0A0E0B]" fill="#0A0E0B" />
              ) : (
                <Play className="w-5 h-5 text-[#0A0E0B] ml-0.5" fill="#0A0E0B" />
              )}
            </button>
            <div className="flex-1 min-w-0">
              <div className="font-anton text-sm uppercase text-[#E5E5E5] tracking-tight truncate">{TRACK.title}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#DD5422]">{TRACK.subtitle}</div>
              <div className="h-1 bg-[#2C332A] mt-1.5 relative">
                <div className="h-full bg-[#DD5422] transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
            <button
              data-testid="theme-mute-toggle"
              onClick={toggleMute}
              className="text-[#8D9688] hover:text-[#DD5422] p-1"
              aria-label="Mute"
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <button
              data-testid="theme-collapse"
              onClick={() => setExpanded(false)}
              className="text-[#8D9688] hover:text-[#DD5422] px-1 font-anton text-lg"
              aria-label="Hide"
            >
              ×
            </button>
          </div>
        </div>
      ) : (
        <button
          data-testid="theme-expand"
          onClick={() => setExpanded(true)}
          className="w-12 h-12 bg-[#DD5422] border-2 border-[#0A0E0B] hover:bg-[#FF6B35] flex items-center justify-center shadow-2xl relative"
          aria-label="Open theme song player"
        >
          <Music2 className="w-5 h-5 text-[#0A0E0B]" strokeWidth={2.5} />
          {playing && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-[#4CAF50] rounded-full animate-pulse" />
          )}
        </button>
      )}
    </div>
  );
}
